// DocsPage.tsx — one repository's recorded documentation, in either of two
// shapes.
//
// `split` is the default: the tree on the left, the selected document on the
// right. The two halves are read together — a directory's summary is assembled
// from its children's, so the question asked of any page is immediately
// "assembled from what?", and the answer is the subtree still on screen beside
// it.
//
// `full` gives the whole width to one of them at a time, at a larger size:
// the tree to walk, or the document to read. On a repository of two thousand
// nodes the tree in a five-column gutter is a keyhole, and the narrow pane
// that makes the document legible is the same pane that makes the tree
// unreadable. So this is a drill-in — pick a node, read it, come back — which
// is what "one pane" has to mean if it is to mean anything.
//
// Both shapes keep the tree's DATA mounted across the switch. Only the layout
// changes, so toggling never costs a request and never loses which
// directories the reader had opened.
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { BsArrowLeft, BsArrowsFullscreen, BsLayoutSidebarInset } from 'react-icons/bs';
import { StatusBadge } from 'common';
import { fetchDocBlob, fetchDocTree, fetchSource } from '../api';
import type { DocBlob, DocNode, SourceFile } from '../api';
import { useAuth } from 'common';
import { apiClient, isAdmin } from '../api';
import { useLayoutMode } from '../layout';
import type { LayoutMode } from '../layout';
import DocMeta from '../components/DocMeta';
import DocProse, { buildPathIndex } from '../components/DocProse';
import DocSource from '../components/DocSource';
import Cleanups, { findNode } from '../components/Cleanups';
import DocTree from '../components/DocTree';

// TreeCounts is what the tree card's footer reports: how much of the
// repository has been walked, and how much of what was walked has a summary.
// The gap between the two is the honest measure of how far behind the daemon
// is on this repository — a number no single node can show.
interface TreeCounts {
  dirs: number;
  files: number;
  documented: number;
}

function countNodes(node: DocNode, into: TreeCounts): TreeCounts {
  if (node.kind === 'dir') into.dirs += 1;
  else into.files += 1;
  if (node.has_doc) into.documented += 1;
  for (const child of node.children ?? []) countNodes(child, into);
  return into;
}

// firstDocumented picks what to render before the reader has chosen anything.
// The repository root is the right answer when it has a summary — it is the
// document the whole tree reduces into — and the first documented node in
// reading order is the right answer when it does not.
// parentDir is the directory a file sits in, so a bare name in its page
// resolves beside it before anywhere else.
function parentDir(p: string): string {
  const idx = p.lastIndexOf('/');
  return idx === -1 ? '' : p.slice(0, idx);
}

function firstDocumented(node: DocNode): DocNode | null {
  if (node.has_doc) return node;
  for (const child of node.children ?? []) {
    const found = firstDocumented(child);
    if (found) return found;
  }
  return null;
}

export default function DocsPage() {
  const { owner = '', name = '' } = useParams();
  const [tree, setTree] = useState<DocNode | null>(null);
  const [selected, setSelected] = useState<DocNode | null>(null);
  const [blob, setBlob] = useState<DocBlob | null>(null);
  const [treeError, setTreeError] = useState('');
  const [blobError, setBlobError] = useState('');
  const auth = useAuth({ client: apiClient });
  const [mode, setMode] = useLayoutMode();
  // Which pane the full-width shape is showing. Meaningless in `split`, where
  // both are on screen — kept in one place anyway so switching modes mid-read
  // lands somewhere deliberate rather than wherever the last render left off.
  const [fullPane, setFullPane] = useState<'tree' | 'doc'>('tree');
  // Which half of a node is being read: the page docdaemon wrote, or the file
  // it wrote it from. A directory has no source, so the tab is only offered
  // for files and resets when the selection changes kind.
  const [view, setView] = useState<'summary' | 'source'>('summary');
  const [source, setSource] = useState<SourceFile | null>(null);
  const [sourceError, setSourceError] = useState('');

  useEffect(() => {
    let live = true;
    setTree(null);
    setSelected(null);
    // Clears with the tree, not only on success: navigating from a repository
    // whose tree 403'd to one that loads would otherwise keep the first
    // repository's error over the second one's documentation.
    setTreeError('');
    void (async () => {
      try {
        const root = await fetchDocTree(owner, name);
        if (!live) return;
        setTree(root);
        setSelected(firstDocumented(root));
      } catch (err) {
        if (live) setTreeError(err instanceof Error ? err.message : 'Failed to load the documentation tree');
      }
    })();
    return () => { live = false; };
  }, [owner, name]);

  // Keyed on the path rather than on the node object: selecting the same node
  // twice must not re-fetch, and two nodes are the same document exactly when
  // their source paths match.
  const path = selected?.path ?? null;
  useEffect(() => {
    if (path === null) return undefined;
    let live = true;
    setBlob(null);
    setBlobError('');
    void (async () => {
      try {
        const doc = await fetchDocBlob(owner, name, path);
        if (live) setBlob(doc);
      } catch (err) {
        if (live) setBlobError(err instanceof Error ? err.message : 'Failed to load the document');
      }
    })();
    return () => { live = false; };
  }, [owner, name, path]);

  // The source is fetched only when it is actually being looked at: most
  // reading is of the pages, and pre-fetching every file behind every summary
  // would pull a repository through the tunnel for nothing.
  useEffect(() => {
    if (view !== 'source' || !selected || selected.kind !== 'file') return undefined;
    let live = true;
    setSource(null);
    setSourceError('');
    void (async () => {
      try {
        const file = await fetchSource(owner, name, selected.path);
        if (live) setSource(file);
      } catch (err) {
        if (live) setSourceError(err instanceof Error ? err.message : 'Failed to read the file');
      }
    })();
    return () => { live = false; };
  }, [owner, name, view, selected]);

  // Indexed once per tree, not per render: a repository of two thousand nodes
  // is walked to build this and the page re-renders on every selection.
  const pathIndex = useMemo(() => buildPathIndex(tree), [tree]);

  const counts = tree ? countNodes(tree, { dirs: 0, files: 0, documented: 0 }) : null;

  // Selecting is what drills in. In `split` the document simply appears
  // beside the tree, so the pane state is set anyway and is just unused —
  // which is what makes switching to `full` mid-read land on the document
  // being read rather than back at the root.
  const select = (node: DocNode) => {
    setSelected(node);
    setFullPane('doc');
    // Back to the summary on every move. Carrying the source tab across a
    // selection would open the next file at its code, which is the opposite
    // of what a documentation browser is for.
    setView('summary');
  };

  // Switching to full-width shows the TREE, not the document that happened to
  // be auto-selected on load. The tree at full width is the thing being asked
  // for; opening straight into a document nobody chose would hide it.
  const chooseMode = (next: LayoutMode) => {
    if (next === 'full') setFullPane('tree');
    setMode(next);
  };

  const treeCard = (
    <Card>
      <Card.Header>Documentation Tree</Card.Header>
      <Card.Body>
        {tree ? (
          <DocTree
            tree={tree}
            selected={selected?.path ?? null}
            onSelect={select}
            roomy={mode === 'full'}
          />
        ) : treeError ? (
          <p className="text-muted small m-0">Nothing to show.</p>
        ) : (
          <div className="d-flex justify-content-center py-4"><Spinner animation="border" size="sm" /></div>
        )}
      </Card.Body>
      <Card.Footer className="small text-muted">
        {counts
          ? <>{counts.dirs} director{counts.dirs === 1 ? 'y' : 'ies'} · {counts.files} file
            {counts.files === 1 ? '' : 's'} · {counts.documented} documented</>
          : 'Walking the repository…'}
      </Card.Footer>
    </Card>
  );

  const docCard = (
    <Card>
      <Card.Header className="d-flex align-items-center justify-content-between gap-2">
        <span className="d-inline-flex align-items-center gap-2 text-truncate">
          {/* Only in the one-pane shape, where the tree is not on screen to
              go back to by eye. In split it would be a button that undoes
              nothing. */}
          {mode === 'full' && (
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none flex-shrink-0"
              onClick={() => setFullPane('tree')}
            >
              <span className="d-inline-flex align-items-center gap-1">
                <BsArrowLeft /> Tree
              </span>
            </Button>
          )}
          <code className="text-truncate">{selected ? (selected.path || '/') : 'No document selected'}</code>
        </span>
        <span className="d-inline-flex align-items-center gap-2 flex-shrink-0">
          {/* Only files have a source to show. A directory's "contents" are
              its children, which is what the tree already is. */}
          {selected?.kind === 'file' && (
            <ButtonGroup size="sm" aria-label="What to show">
              <Button
                variant={view === 'summary' ? 'primary' : 'outline-primary'}
                onClick={() => setView('summary')}
                aria-pressed={view === 'summary'}
              >
                Summary
              </Button>
              <Button
                variant={view === 'source' ? 'primary' : 'outline-primary'}
                onClick={() => setView('source')}
                aria-pressed={view === 'source'}
              >
                Source
              </Button>
            </ButtonGroup>
          )}
          {selected && (
            <StatusBadge tone="neutral" uppercase={false}>
              {selected.kind}
            </StatusBadge>
          )}
        </span>
      </Card.Header>
      <Card.Body>
        {blobError ? (
          <Alert variant="danger" className="m-0">{blobError}</Alert>
        ) : !selected ? (
          <p className="text-muted m-0">
            Nothing here has a summary yet. A run writes them deepest-file-first, so the
            first thing to appear is a file, and the repository root is the last.
          </p>
        ) : !blob ? (
          <div className="d-flex justify-content-center py-4"><Spinner animation="border" size="sm" /></div>
        ) : view === 'source' ? (
          <DocSource file={source} error={sourceError} />
        ) : (
          <>
            {/* Provenance first: it is what says whether to trust the page
                below it. The server strips the same facts out of the body, so
                this is the only place they appear. */}
            <DocMeta blob={blob} />
            {/* The one piece of running text in the app, so the one place
                the reading cap applies (DESIGN.md C-LAYOUT-2) — the tree
                beside it takes the width it needs. Paths inside it that name
                a real node in this repository are clickable. */}
            <DocProse
              className="prose-column"
              content={blob.content}
              index={pathIndex}
              fromDir={selected.kind === 'dir' ? selected.path : parentDir(selected.path)}
              onSelect={select}
            />
          </>
        )}
      </Card.Body>
      <Card.Footer className="small text-muted">
        {/* The when/model/hash moved to the strip above the prose; what is
            left here is where the page lives, which is a fact about the
            repository rather than about the generation. */}
        {blob
          ? <>Describes <code>{blob.path || '/'}</code> · committed at <code>{blob.doc_path}</code></>
          : 'A document carries the model that wrote it and a hash of the input it was written from.'}
      </Card.Footer>
    </Card>
  );

  // Scoped to the directory being read: a file's findings belong to the
  // directory it sits in, which is the unit a review is run over.
  const reviewDir = selected
    ? (selected.kind === 'dir' ? selected.path : parentDir(selected.path))
    : '';
  const cleanupsCard = (
    <Cleanups
      owner={owner}
      name={name}
      dir={reviewDir}
      isAdmin={isAdmin(auth)}
      onSelectPath={(p) => {
        const node = findNode(tree, p);
        if (node) select(node);
      }}
    />
  );

  return (
    <div>
      {/* The mode control sits outside the cards, beside the breadcrumb: it
          changes the shape of the page, so it does not belong to either of the
          cards whose shape it changes. */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <Breadcrumb className="mb-0">
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/repos' }}>Repositories</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/repos/${owner}/${name}` }}>
            {owner}/{name}
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Documentation</Breadcrumb.Item>
        </Breadcrumb>
        <ButtonGroup size="sm" aria-label="Documentation layout">
          <Button
            variant={mode === 'split' ? 'primary' : 'outline-primary'}
            onClick={() => chooseMode('split')}
            aria-pressed={mode === 'split'}
            title="Tree and document side by side"
          >
            <span className="d-inline-flex align-items-center gap-2">
              <BsLayoutSidebarInset /> Split
            </span>
          </Button>
          <Button
            variant={mode === 'full' ? 'primary' : 'outline-primary'}
            onClick={() => chooseMode('full')}
            aria-pressed={mode === 'full'}
            title="One pane at a time, full width and larger"
          >
            <span className="d-inline-flex align-items-center gap-2">
              <BsArrowsFullscreen /> Full width
            </span>
          </Button>
        </ButtonGroup>
      </div>

      {treeError && <Alert variant="danger" className="mt-3">{treeError}</Alert>}

      {mode === 'split' ? (
        <Row className="g-3 mt-0">
          <Col lg={5} xxl={4}>{treeCard}</Col>
          <Col lg={7} xxl={8}>
            {docCard}
            <div className="mt-3">{cleanupsCard}</div>
          </Col>
        </Row>
      ) : fullPane === 'tree' ? (
        <div className="mt-3">{treeCard}</div>
      ) : (
        <div className="mt-3">
          {docCard}
          <div className="mt-3">{cleanupsCard}</div>
        </div>
      )}
    </div>
  );
}
